/**
 * 用户认证 Store
 *
 * 已接入 Cloudflare Workers 后端:
 *   - sendCode → POST /api/auth/send-code
 *   - login → POST /api/auth/login
 *   - 失败时降级到 localStorage 模拟(开发模式)
 *
 * 后端不开时自动用本地模式,不会阻塞用户
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api, getApiBase, setApiBase as saveApiBase } from '../lib/api.js';

// 后端连接状态(5 秒过期,避免缓存 false 永远走本地)
let backendOnline = null;
let backendOnlineTime = 0;
async function isBackendOnline() {
  if (backendOnline !== null && Date.now() - backendOnlineTime < 5000) return backendOnline;
  try {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 4000);
    const res = await fetch(`${getApiBase()}/api/health`, { signal: ctrl.signal });
    clearTimeout(timer);
    backendOnline = res.ok;
    backendOnlineTime = Date.now();
  } catch {
    backendOnline = false;
    backendOnlineTime = Date.now();
  }
  return backendOnline;
}

// 本地模拟 SMS(后端不可用时)
const DEV_VERIFY_CODE = '123456';
async function localSendSms(phone) {
  const code = String(Math.floor(100000 + Math.random() * 900000));
  const expires = Date.now() + 5 * 60 * 1000;
  localStorage.setItem(`sms_code_${phone}`, JSON.stringify({ code, expires }));
  await new Promise(r => setTimeout(r, 600));
  return { ok: true, _devCode: code, _backend: false };
}
async function localVerifySms(phone, code) {
  if (code === DEV_VERIFY_CODE) return { ok: true };
  const stored = localStorage.getItem(`sms_code_${phone}`);
  if (!stored) return { ok: false, msg: '验证码已过期,请重新获取' };
  const { code: realCode, expires } = JSON.parse(stored);
  if (Date.now() > expires) return { ok: false, msg: '验证码已过期' };
  if (code !== realCode) return { ok: false, msg: '验证码错误' };
  localStorage.removeItem(`sms_code_${phone}`);
  return { ok: true };
}

export const useUser = create(
  persist(
    (set, get) => ({
      user: null,  // { id, phone, name, tier, token, ... }
      loading: false,
      backend: 'unknown',  // 'online' / 'offline' / 'unknown'

      // 发送验证码(优先后端,失败兜底本地)
      sendCode: async (phone) => {
        set({ loading: true });
        try {
          if (await isBackendOnline()) {
            const res = await api.sendCode(phone);
            set({ loading: false, backend: 'online' });
            return { ok: true, _devCode: res._devCode, _backend: true };
          }
        } catch (e) {
          console.warn('后端发送失败,降级本地:', e.message);
          backendOnline = false;
        }
        // 本地兜底
        const r = await localSendSms(phone);
        set({ loading: false, backend: 'offline' });
        return r;
      },

      // 验证码登录(优先后端,失败兜底本地)
      login: async (phone, code) => {
        set({ loading: true });
        // 先试后端
        if (await isBackendOnline()) {
          try {
            const res = await api.login(phone, code);
            set({ user: res.user, token: res.token, loading: false, backend: 'online' });
            // 同步到 localStorage(模拟用户列表)
            try {
              const allUsers = JSON.parse(localStorage.getItem('fucai3d_all_users') || '[]');
              const idx = allUsers.findIndex(u => u.phone === phone);
              if (idx >= 0) allUsers[idx] = res.user;
              else allUsers.push(res.user);
              localStorage.setItem('fucai3d_all_users', JSON.stringify(allUsers));
            } catch {}
            return { ok: true, isNew: !res.user.createdAt, _backend: true };
          } catch (e) {
            console.warn('后端登录失败:', e.message);
            // 后端真实存在(用户已在 D1):不要本地兜底,直接报错,避免误导用户
            set({ loading: false, backend: 'online' });
            return { ok: false, error: e.message || '登录失败', _backend: true };
          }
        }
        // 本地兜底(仅 backend 完全不可达时)
        const verifyRes = await localVerifySms(phone, code);
        if (verifyRes.ok) {
          const existing = localStorage.getItem(`user_${phone}`);
          const now = Date.now();
          const TRIAL_MS = 24 * 60 * 60 * 1000;
          let user;
          if (existing) {
            user = { ...JSON.parse(existing), lastLogin: now };
          } else {
            user = {
              id: Date.now(),  // 临时 ID
              phone,
              name: `用户${phone.slice(-4)}`,
              tier: 'Pro 月付',
              trialStart: now,
              trialExpiresAt: now + TRIAL_MS,
              createdAt: now,
              lastLogin: now,
              status: 'active',
            };
            try {
              const allUsers = JSON.parse(localStorage.getItem('fucai3d_all_users') || '[]');
              allUsers.push(user);
              localStorage.setItem('fucai3d_all_users', JSON.stringify(allUsers));
            } catch {}
          }
          localStorage.setItem(`user_${phone}`, JSON.stringify(user));
          set({ user, loading: false, backend: 'offline' });
          return { ok: true, isNew: !existing, _backend: false };
        }
        set({ loading: false });
        return verifyRes;
      },

      logout: () => {
        set({ user: null, token: null });
      },

      // 直接覆盖 user 对象(支付后用)
      setUser: (user) => {
        if (!user) return;
        try {
          if (user.phone) {
            localStorage.setItem(`user_${user.phone}`, JSON.stringify(user));
          }
        } catch {}
        set({ user });
      },

      // 杀号(用户手动标记的号码,在选号和大底池中排除)
      setUserKills: (kills) => {
        const { user } = get();
        if (!user) {
          // 未登录:存 localStorage
          try { localStorage.setItem('fucai3d_user_kills', JSON.stringify(kills)); } catch {}
          return;
        }
        const updated = { ...user, userKills: kills };
        set({ user: updated });
        try { localStorage.setItem(`user_${user.phone}`, JSON.stringify(updated)); } catch {}
      },

      // 升级会员(暂时本地切换,后期接支付再改)
      upgrade: (tier, expiresAt) => {
        const { user } = get();
        if (!user) return;
        const updated = { ...user, tier, expiresAt: expiresAt || null, upgradedAt: Date.now() };
        try {
          localStorage.setItem(`user_${user.phone}`, JSON.stringify(updated));
        } catch {}
        set({ user: updated });
      },

      // 试用检查
      checkTrial: () => {
        const { user } = get();
        if (!user) return { onTrial: false, hoursLeft: 0, expired: false };

        // 已付费(tier_expires_at 在未来 或 永久买断)→ 不算试用
        const now = Date.now();
        const isPaid = user.tier_expires_at && user.tier_expires_at > now;
        const isPermanent = user.tier && (
          user.tier.includes('买断') ||
          user.tier === 'MAX 买断' ||
          user.tier === 'Pro 永久' ||
          user.tier === '永久买断'
        );
        if (isPaid || isPermanent) {
          return { onTrial: false, hoursLeft: 0, expired: false };
        }

        // 后端用户用 trialExpiresAt,本地用 trialStart
        if (user.trialExpiresAt) {
          const hoursPassed = (Date.now() - (user.createdAt || Date.now())) / 3600000;
          const hoursLeft = Math.max(0, Math.ceil(24 - hoursPassed));
          return {
            onTrial: hoursPassed <= 24,
            hoursLeft,
            expired: hoursPassed > 24,
          };
        }
        if (!user.trialStart) return { onTrial: false, hoursLeft: 0, expired: false };
        const hoursPassed = (Date.now() - user.trialStart) / 3600000;
        const hoursLeft = Math.max(0, Math.ceil(24 - hoursPassed));
        return {
          onTrial: hoursPassed <= 24,
          hoursLeft,
          expired: hoursPassed > 24,
        };
      },

      // 重新检测后端
      checkBackend: async () => {
        backendOnline = null;
        const ok = await isBackendOnline();
        set({ backend: ok ? 'online' : 'offline' });
        return ok;
      },
    }),
    {
      name: 'fucai3d_max_user',
      partialize: (s) => ({ user: s.user, token: s.token }),
    }
  )
);

// 管理员用:获取所有用户列表(本地 + 后端)
export function getAllUsers() {
  try {
    return JSON.parse(localStorage.getItem('fucai3d_all_users') || '[]');
  } catch {
    return [];
  }
}
